import type {WizardData} from '../index'

/**
 * Curriculum on levels (docs: curriculum-on-levels-frontend.md, node-api 69463164d).
 *
 * The backend identifies a level by branch + curriculum + level name, so two curricula in one
 * branch can both have "Primary" and "Grade 1-A". The wizard already knows which curriculum
 * every level belongs to (`levelsByCurriculum`, `classes[].curriculum`); these helpers turn that
 * into the `curriculum` / `curriculum_code` / `curriculum_short_code` fields of the bulk-import
 * payload, key the per-level id maps by curriculum, and label a name shared by two curricula
 * for display ("Grade 1-A (FBISE)") without ever changing the stored name.
 */

type ManualData = WizardData['manualData']
type Loose = Record<string, any>

const trim = (value: unknown): string => String(value ?? '').trim()
const lower = (value: unknown): string => trim(value).toLowerCase()
const SEP = '|||'

// ---------------------------------------------------------------------------------------------
// FE-1 · backend support
// ---------------------------------------------------------------------------------------------

/**
 * Release switch for FE-3 (`plain_names=true` on the prefill calls). Stays off until the
 * backend has run the per-school migration that strips the curriculum prefix from existing
 * level/grade/class names: asking for plain names before that makes the prefill disagree with
 * the stored (prefixed) names. Everything else in this module is safe with it off.
 */
export const PLAIN_CURRICULUM_NAMES_RELEASED = false

export type LevelCurriculumSupport = 'supported' | 'unsupported' | 'unknown'

/**
 * Whether the school's database stores a curriculum per level, read from GET
 * onboarding/resume-data: every level then carries a `curriculum` key (null when unset). Keys
 * absent means no support; a school with no levels yet cannot tell ("unknown").
 */
export const detectLevelCurriculumSupport = (apiResult: unknown): LevelCurriculumSupport => {
  const result = (apiResult as Loose)?.result ?? (apiResult as Loose)?.data ?? apiResult ?? {}
  const campuses: Loose[] = Array.isArray((result as Loose)?.campuses) ? result.campuses : []
  const levels = campuses.flatMap((campus) => (Array.isArray(campus?.levels) ? campus.levels : []))
  if (levels.length === 0) return 'unknown'
  return levels.some((level: Loose) => level && typeof level === 'object' && 'curriculum' in level)
    ? 'supported'
    : 'unsupported'
}

/** FE-3 gate: plain names only with the release switch on AND confirmed backend support. */
export const shouldRequestPlainNames = (support: LevelCurriculumSupport): boolean =>
  PLAIN_CURRICULUM_NAMES_RELEASED && support === 'supported'

// ---------------------------------------------------------------------------------------------
// Curriculum fields
// ---------------------------------------------------------------------------------------------

/** "Federal Board (FBISE)" → "FBISE"; null when the name has no trailing bracket. */
export const bracketedShortForm = (curriculum: unknown): string | null => {
  const match = /\(([^()]+)\)\s*$/.exec(trim(curriculum))
  return match ? trim(match[1]) || null : null
}

/**
 * The short label shown beside a shared name (FE-5): the catalogue short code, else the
 * bracketed part of the curriculum name, else the full name. Display only.
 */
export const curriculumShortLabel = (
  curriculum: unknown,
  shortCodes: Record<string, string> | undefined
): string => {
  const name = trim(curriculum)
  if (!name) return ''
  return trim(shortCodes?.[name]) || bracketedShortForm(name) || name
}

export type CurriculumFields = {
  curriculum?: string
  curriculum_code?: string
  curriculum_short_code?: string
}

/**
 * `curriculum` (the display name, exactly as saved in the wizard) plus, when `withCodes`, the
 * catalogue code and short code. Empty for no curriculum or the "Default Curriculum"
 * placeholder, which is not a real curriculum and must never be stored on a level.
 */
export const curriculumFieldsFor = (
  curriculum: unknown,
  manualData: Pick<ManualData, 'curriculumCodes' | 'curriculumShortCodes'>,
  withCodes = false
): CurriculumFields => {
  const name = trim(curriculum)
  if (!name || name === PLACEHOLDER_CURRICULUM) return {}
  const fields: CurriculumFields = {curriculum: name}
  if (!withCodes) return fields
  const code = trim(manualData?.curriculumCodes?.[name])
  const shortCode = trim(manualData?.curriculumShortCodes?.[name])
  if (code) fields.curriculum_code = code
  if (shortCode) fields.curriculum_short_code = shortCode
  return fields
}

/** Mirrors `FALLBACK_CURRICULUM` (mapOnboardingData) without importing it (circular). */
const PLACEHOLDER_CURRICULUM = 'Default Curriculum'

// ---------------------------------------------------------------------------------------------
// Which curriculum a (branch, level) belongs to
// ---------------------------------------------------------------------------------------------

export type LevelCurriculumLookup = {
  /** Curricula that have a level of this name in this branch (lower-cased keys, real names out). */
  curriculaFor: (branch: unknown, level: unknown) => string[]
  /**
   * The curriculum to send for a row naming (branch, level): the row's own curriculum when it
   * has one, else the only curriculum with that level in the branch. Undefined when the level
   * is shared by several curricula and the row does not say which — never a guess.
   */
  resolve: (branch: unknown, level: unknown, hint?: unknown) => string | undefined
  /** True when two or more curricula have a level of this name in the branch. */
  isShared: (branch: unknown, level: unknown) => boolean
}

/**
 * Builds the (branch, level) → curriculum lookup from the curriculum config and the class rows.
 * `branches` is the wizard's branch list; a curriculum with the same levels in every branch
 * contributes its levels to each of them.
 */
export const buildLevelCurriculumLookup = (manualData: ManualData): LevelCurriculumLookup => {
  const byKey = new Map<string, Map<string, string>>()
  const add = (branch: unknown, level: unknown, curriculum: unknown) => {
    const c = trim(curriculum)
    if (!trim(level) || !c || c === PLACEHOLDER_CURRICULUM) return
    const key = `${lower(branch)}${SEP}${lower(level)}`
    const entry = byKey.get(key) ?? new Map<string, string>()
    if (!entry.has(lower(c))) entry.set(lower(c), c)
    byKey.set(key, entry)
  }
  const branches = ((manualData?.branches ?? []) as unknown[])
    .map((entry: any) => trim(typeof entry === 'string' ? entry : entry?.name))
    .filter(Boolean)
  const effectiveBranches = branches.length > 0 ? branches : ['Main Branch']

  Object.entries((manualData?.levelsByCurriculum ?? {}) as Record<string, Loose>).forEach(
    ([curriculum, cfg]) => {
      if (cfg?.levelsSameAcrossBranches === false) {
        Object.entries((cfg?.levelsPerBranch ?? {}) as Record<string, unknown[]>).forEach(
          ([branch, names]) => (names ?? []).forEach((name) => add(branch, name, curriculum))
        )
        return
      }
      ;((cfg?.levels ?? []) as Loose[]).forEach((level) =>
        effectiveBranches.forEach((branch) => add(branch, level?.name, curriculum))
      )
    }
  )
  ;((manualData?.classes ?? []) as Loose[]).forEach((row) =>
    add(row?.branch || effectiveBranches[0], row?.level, row?.curriculum)
  )

  const curriculaFor = (branch: unknown, level: unknown) =>
    Array.from(byKey.get(`${lower(branch)}${SEP}${lower(level)}`)?.values() ?? [])

  return {
    curriculaFor,
    isShared: (branch, level) => curriculaFor(branch, level).length > 1,
    resolve: (branch, level, hint) => {
      const own = trim(hint)
      if (own && own !== PLACEHOLDER_CURRICULUM) return own
      const candidates = curriculaFor(branch, level)
      return candidates.length === 1 ? candidates[0] : undefined
    },
  }
}

// ---------------------------------------------------------------------------------------------
// Id maps keyed by curriculum
// ---------------------------------------------------------------------------------------------

/**
 * Key of a level in `branchLevelIdMap[branch]`: `"Primary|||Federal Board (FBISE)"` when the
 * curriculum is known, the bare name otherwise (legacy entries and responses without one).
 */
export const levelIdKey = (level: unknown, curriculum?: unknown): string =>
  trim(curriculum) ? `${trim(level)}${SEP}${trim(curriculum)}` : trim(level)

/** Splits a `branchLevelIdMap` key back into level name and curriculum. */
export const parseLevelIdKey = (key: string): {name: string; curriculum?: string} => {
  const [name, curriculum] = key.split(SEP)
  return {name: trim(name), ...(trim(curriculum) ? {curriculum: trim(curriculum)} : {})}
}

/**
 * Server id of a level from one branch's id map: the curriculum-qualified entry first; the
 * bare-name entry only when the name is not shared by another curriculum in that branch (it
 * could be the other curriculum's level, and an update by that id would move it).
 */
export const lookupLevelId = (
  levelIds: Record<string, number> | undefined,
  level: unknown,
  curriculum: unknown,
  shared: boolean
): number | null => {
  const entries = Object.entries(levelIds ?? {})
  const find = (key: string) => entries.find(([k]) => lower(k) === lower(key))?.[1]
  const qualified = trim(curriculum) ? find(levelIdKey(level, curriculum)) : undefined
  const candidate = qualified ?? (shared && trim(curriculum) ? undefined : find(trim(level)))
  const id = Number(candidate)
  return Number.isFinite(id) && id > 0 ? id : null
}

/**
 * Key of a subject in `subjectIdByNameAndClass`: `"English|||Grade 1-A"`, plus the branch and
 * the curriculum when known (`"English|||Grade 1-A|||@Gulberg|||Federal Board (FBISE)"`). Every
 * branch has its own "Grade 1-A" and the same class name can exist under two curricula, each
 * with its own subject row. The branch segment is marked with `@` so a branch-only key never
 * reads as a curriculum-only one.
 */
export const subjectIdKey = (
  subject: unknown,
  className: unknown,
  curriculum?: unknown,
  branch?: unknown
): string =>
  [
    trim(subject),
    trim(className),
    ...(trim(branch) ? [`@${trim(branch)}`] : []),
    ...(trim(curriculum) ? [trim(curriculum)] : []),
  ].join(SEP)

/**
 * Subject id for (subject, class, curriculum, branch). With a branch, only that branch's entries
 * are read — a branch-less entry is another branch's id just as often (state saved before ids
 * were keyed by branch), and sending it as this class's id dropped the row as "already saved".
 * Without a branch (legacy rows): the curriculum-qualified entry first, then the bare entry.
 */
export const lookupSubjectId = (
  map: Record<string, number> | undefined,
  subject: unknown,
  className: unknown,
  curriculum?: unknown,
  branch?: unknown
): number | null => {
  const source = map ?? {}
  const read = (b: unknown) =>
    (trim(curriculum) ? source[subjectIdKey(subject, className, curriculum, b)] : undefined) ??
    source[subjectIdKey(subject, className, undefined, b)]
  const id = Number(trim(branch) ? read(branch) : read(undefined))
  return Number.isFinite(id) && id > 0 ? id : null
}

// ---------------------------------------------------------------------------------------------
// FE-5 · display a shared name with its curriculum beside it
// ---------------------------------------------------------------------------------------------

/**
 * Labeller for level/class names: a name that exists under more than one curriculum in the
 * same branch gets the curriculum's short label beside it ("Grade 1-A (FBISE)"); a unique name
 * stays bare. `rows` are class rows (`name`, `level`, `branch`, `curriculum`); both the class
 * name and the level name are checked. Display only — never send a label as a name.
 */
export const buildSharedNameLabeler = (
  rows: ReadonlyArray<Loose>,
  shortCodes: Record<string, string> | undefined,
  extraLevels: ReadonlyArray<{branch?: unknown; name: unknown; curriculum: unknown}> = []
) => {
  const curriculaByName = new Map<string, Set<string>>()
  const note = (branch: unknown, name: unknown, curriculum: unknown) => {
    const c = trim(curriculum)
    if (!trim(name) || !c || c === PLACEHOLDER_CURRICULUM) return
    const key = `${lower(branch)}${SEP}${lower(name)}`
    const set = curriculaByName.get(key) ?? new Set<string>()
    set.add(lower(c))
    curriculaByName.set(key, set)
  }
  rows.forEach((row) => {
    note(row?.branch, row?.name, row?.curriculum)
    note(row?.branch, row?.level, row?.curriculum)
  })
  extraLevels.forEach((level) => note(level.branch, level.name, level.curriculum))

  /** True when `name` is shared by curricula in `branch` (any branch when none is given). */
  const isShared = (name: unknown, branch?: unknown): boolean => {
    if (trim(branch))
      return (curriculaByName.get(`${lower(branch)}${SEP}${lower(name)}`)?.size ?? 0) > 1
    return Array.from(curriculaByName.entries()).some(
      ([key, set]) => key.endsWith(`${SEP}${lower(name)}`) && set.size > 1
    )
  }

  return (displayName: string, fullName: unknown, curriculum: unknown, branch?: unknown) => {
    if (!trim(curriculum) || !isShared(fullName, branch)) return displayName
    const label = curriculumShortLabel(curriculum, shortCodes)
    return label ? `${displayName} (${label})` : displayName
  }
}

/**
 * True when `hint` (a curriculum as written in a template label: short code, bracketed short
 * form or full name) names `curriculum`.
 */
export const curriculumMatchesHint = (
  curriculum: unknown,
  hint: unknown,
  shortCodes: Record<string, string> | undefined
): boolean => {
  const wanted = lower(hint)
  if (!wanted) return true
  const name = trim(curriculum)
  if (!name) return false
  return [name, shortCodes?.[name], bracketedShortForm(name)].some(
    (candidate) => lower(candidate) === wanted
  )
}
